import { EventData, EventDataTypes } from "lib/tracking";
import { CognismContactFilters } from "./cognismContactFilters";

/**
 * Shared Amplitude attributes for the unified contacts table, so the three reveal
 * paths (row eye, bulk action bar, export) report the same shape and can be compared
 * in one funnel. Attribute names match the legacy contacts table (`trackContactData`
 * in ContactsTableUtils) so both variants aggregate together.
 */

/** Where a reveal came from — every reveal event splits on this. */
export type RevealTrigger = "Row" | "Bulk" | "Export" | "Lead suggestion";

/** Per-item outcome of a reveal call; suppressed/failed rows cost no credit. */
export type RevealOutcome = {
  revealedCount: number;
  suppressedCount: number;
  failedCount: number;
};

/** Reveal attributes: what was asked for, what came back, and where from. */
export function revealEventData(trigger: RevealTrigger, outcome: RevealOutcome): EventData {
  return {
    "Data type": EventDataTypes.contact,
    Trigger: trigger,
    "Revealed count": outcome.revealedCount,
    "Suppressed count": outcome.suppressedCount,
    "Failed count": outcome.failedCount,
  };
}

/**
 * The attributes a per-contact event reports. Structural rather than tied to `ContactSearchResult`,
 * so the lead's contact cards report the same shape from a suggestion or a saved contact.
 */
export type ContactEventContext = {
  source: string;
  jobTitle?: string | null;
  seniorityTier?: string | null;
  jobFunction?: readonly string[] | null;
  buyer?: { name: string } | null;
  account?: { name: string } | null;
  oscarOrgName?: string | null;
};

/** Per-contact context for single-row events (reveal, copy, LinkedIn). */
export function contactEventData(contact: ContactEventContext): EventData {
  const data: EventData = {
    "Contact source": contact.source === "oscar" ? "Oscar" : "Cognism",
  };
  if (contact.jobTitle) data["Contact job title"] = contact.jobTitle;
  if (contact.seniorityTier) data["Contact seniority"] = contact.seniorityTier;
  if (contact.jobFunction?.length) data["Contact function"] = contact.jobFunction.join(", ");
  const buyerName = contact.buyer?.name ?? contact.account?.name ?? contact.oscarOrgName;
  if (buyerName) data["Buyer name"] = buyerName;
  return data;
}

type ContactFilterField = Exclude<keyof CognismContactFilters, "searchQuery">;

/**
 * Filter names as "Filter actioned" reports them. Job title, Function and Seniority match the
 * legacy contacts table, so both tables chart together.
 */
const FILTER_NAMES: Record<ContactFilterField, string> = {
  jobTitles: "Job title",
  jobFunctions: "Function",
  seniorityTiers: "Seniority",
  buyerGuids: "Buyer",
  buyerListIds: "Buyer list",
  buyerCategoryIds: "Buyer category",
  hasEmail: "Has email",
  hasMobilePhone: "Has mobile",
  hasDirectPhone: "Has direct phone",
};

// Ids mean nothing in a chart, so these report how many were picked rather than which.
const COUNTED_FIELDS: ContactFilterField[] = ["buyerGuids", "buyerListIds", "buyerCategoryIds"];

function filterSelection(
  field: ContactFilterField,
  value: CognismContactFilters[ContactFilterField],
  seniorityTierLabels: Record<string, string>,
): string {
  if (typeof value === "boolean") return value ? "Yes" : "";
  if (COUNTED_FIELDS.includes(field)) return value.length > 0 ? String(value.length) : "";
  if (field === "seniorityTiers") {
    return value.map((tier) => seniorityTierLabels[tier] ?? tier).join(", ");
  }
  return value.join(", ");
}

function isEmptyFilter(value: CognismContactFilters[ContactFilterField]): boolean {
  return typeof value === "boolean" ? !value : value.length === 0;
}

// Compares the selections themselves: two different buyers report the same count.
function sameFilterValue(
  a: CognismContactFilters[ContactFilterField],
  b: CognismContactFilters[ContactFilterField],
): boolean {
  if (typeof a === "boolean" || typeof b === "boolean") return a === b;
  return a.length === b.length && a.every((value, i) => value === b[i]);
}

export const ALL_FILTERS_CLEARED: EventData = {
  "Filter name": "All filters",
  "Filter selection": "All options",
  "Action type": "Filter cleared",
};

/**
 * "Filter actioned" events for a change to the unified contacts filters, one per filter that
 * changed. Clearing several at once (the "Clear all" buttons) reports once, as "All filters",
 * counting a name search cleared in the same change. Otherwise the name search is left out: it
 * changes per keystroke, so the table reports it once debounced.
 */
export function contactFilterEvents(
  previous: CognismContactFilters,
  next: CognismContactFilters,
  seniorityTierLabels: Record<string, string>,
): EventData[] {
  const changed = (Object.keys(FILTER_NAMES) as ContactFilterField[]).filter(
    (field) => !sameFilterValue(previous[field], next[field]),
  );
  const searchCleared = previous.searchQuery.trim() !== "" && next.searchQuery.trim() === "";
  const clearedCount = changed.length + (searchCleared ? 1 : 0);

  if (clearedCount > 1 && changed.every((field) => isEmptyFilter(next[field]))) {
    return [ALL_FILTERS_CLEARED];
  }

  return changed.map((field) => ({
    "Filter name": FILTER_NAMES[field],
    "Filter selection": filterSelection(field, next[field], seniorityTierLabels),
    "Action type": isEmptyFilter(next[field]) ? "Filter cleared" : "Filter applied",
  }));
}
